import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  deal,
  fetchBalance,
  getInitData,
  loginDev,
  loginWithInitData,
  play,
  takeTokenFromUrl,
  userIdFromToken,
  type DealResponse,
  type PlayRound,
  type AuthUser,
} from './api/client'
import { CardDisplay } from './components/CardDisplay'
import { BetPanel } from './components/BetPanel'
import { ResultOverlay } from './components/ResultOverlay'
import { goLobby } from './lobby'
import { translateApiError } from './i18n'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import brandMark from '../../shared/assets/round6-snowfold-atlas-mark-v1.png'
import hiloWorld from '../../shared/assets/round12-hilo-snowfold-stage-clean-v1.webp'
import { prefersReducedMotion } from '../../shared/motion.ts'

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])

  const [token, setToken] = useState<string | null>(
    () =>
      takeTokenFromUrl() ||
      (import.meta.env.VITE_DEV_TOKEN as string | undefined) ||
      null,
  )
  const [user, setUser] = useState<AuthUser | null>(null)
  const [balance, setBalance] = useState(0)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authAttempt, setAuthAttempt] = useState(0)

  const [currentDeal, setCurrentDeal] = useState<DealResponse | null>(null)
  const [nextCard, setNextCard] = useState<{ rank: number; suit: string } | null>(
    null,
  )
  const [result, setResult] = useState<PlayRound | null>(null)
  const [busy, setBusy] = useState(false)
  const [pendingChoice, setPendingChoice] = useState<'higher' | 'lower' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [balanceStale, setBalanceStale] = useState(false)
  const [balanceOutcomeUnknown, setBalanceOutcomeUnknown] = useState(false)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const balanceRefreshRef = useRef(false)
  const dealInFlightRef = useRef(false)
  const playInFlightRef = useRef(false)

  const displayBalance = useCountUp(balance)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        if (token && !user) {
          const { balance: bal } = await fetchBalance(token)
          if (cancelled) return
          setBalance(bal)
          setUser({
            id: userIdFromToken(token),
            telegram_id: 0,
            username: 'player',
            first_name: 'Player',
            balance: bal,
          })
          setAuthLoading(false)
          return
        }
        const initData = await getInitData()
        const res = initData
          ? await loginWithInitData(initData)
          : await loginDev({ username: 'local_dev', first_name: 'Local' })
        if (cancelled) return
        setToken(res.token)
        setUser(res.user)
        setBalance(res.user.balance)
        setAuthLoading(false)
      } catch {
        if (!cancelled) {
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAttempt])

  const refreshBalance = useCallback(async () => {
    if (!token || balanceRefreshRef.current) return false
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const { balance: bal } = await fetchBalance(token)
      setBalance(bal)
      setBalanceStale(false)
      setBalanceOutcomeUnknown(false)
      return true
    } catch {
      setBalanceStale(true)
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }, [token])

  const onDeal = async () => {
    if (!token || dealInFlightRef.current || busy) return
    // Two deal responses can otherwise arrive out of order, leaving the UI's
    // card different from the server's single pending deal.
    dealInFlightRef.current = true
    setBusy(true)
    setError(null)
    setResult(null)
    setNextCard(null)
    try {
      const d = await deal(token)
      setCurrentDeal(d)
    } catch (e) {
      const raw = e instanceof Error ? e.message : t('dealFailed')
      setError(translateApiError(raw, t))
    } finally {
      dealInFlightRef.current = false
      setBusy(false)
    }
  }

  const onPlay = async (amount: number, choice: 'higher' | 'lower') => {
    if (
      !token ||
      !currentDeal ||
      playInFlightRef.current ||
      busy ||
      balanceStale ||
      balanceRefreshing
    ) return
    playInFlightRef.current = true
    setBusy(true)
    setPendingChoice(choice)
    setError(null)
    try {
      const res = await play(token, {
        base_card: currentDeal.base_card,
        base_suit: currentDeal.base_suit,
        bet_amount: amount,
        choice,
      })
      setNextCard({ rank: res.round.next_card, suit: res.round.next_suit })
      if (!prefersReducedMotion()) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 460))
      }
      setResult(res.round)
      await refreshBalance()
    } catch {
      // The server may have committed the wager even if the response/JSON was
      // lost. Fail closed until an authoritative balance refresh succeeds.
      setBalanceOutcomeUnknown(true)
      setBalanceStale(true)
      setError(null)
    } finally {
      playInFlightRef.current = false
      setPendingChoice(null)
      setBusy(false)
    }
  }

  if (authLoading) {
    return (
      <div className="center">
        <div className="spinner" />
        <p>{t('connecting')}</p>
      </div>
    )
  }

  if (authError || !token) {
    return (
      <div className="center">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <div className="hilo-auth-actions">
          <button
            type="button"
            className="btn-lobby"
            onClick={() => {
              setAuthError(null)
              setAuthLoading(true)
              setAuthAttempt((value) => value + 1)
            }}
          >
            {t('retry')}
          </button>
          <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
            {t('backLobby')}
          </button>
        </div>
      </div>
    )
  }

  const drawerTabs: SheetTab[] = [
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
  ]

  return (
    <div className="app app-single-viewport" data-game="hilo">
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={displayBalance}
        balanceLabel={t('balance', { amount: Math.round(displayBalance) })}
        onOpenRules={() => setSheetOpen(true)}
      />

      <div className="game-stage-viewport">
        <div className="table" style={{ width: '100%', height: '100%', position: 'relative' }}>
          <img className="game-world-backdrop" src={hiloWorld} alt="" aria-hidden />
          <CardDisplay
            label={t('baseCard')}
            rank={currentDeal?.base_card ?? result?.base_card}
            suit={currentDeal?.base_suit ?? result?.base_suit}
            faceDown={!currentDeal && !result}
            flipKey={
              currentDeal
                ? `${currentDeal.base_card}-${currentDeal.base_suit}`
                : result
                  ? `base-${result.id}`
                  : 'empty-base'
            }
            side="left"
          />
          <CardDisplay
            label={t('nextCard')}
            rank={nextCard?.rank ?? result?.next_card}
            suit={nextCard?.suit ?? result?.next_suit}
            faceDown={!nextCard && !result}
            flipKey={
              nextCard
                ? `${nextCard.rank}-${nextCard.suit}`
                : result
                  ? `next-${result.id}`
                  : 'empty-next'
            }
            side="right"
          />
        </div>
      </div>

      <BetPanel
        balance={balance}
        oddsHigher={currentDeal?.odds_higher ?? 0}
        oddsLower={currentDeal?.odds_lower ?? 0}
        busy={busy}
        hasDeal={!!currentDeal}
        onDeal={() => void onDeal()}
        onPlay={(amt, choice) => void onPlay(amt, choice)}
        pendingChoice={pendingChoice}
        balanceStale={balanceStale}
        balanceRefreshing={balanceRefreshing}
        onRetryBalance={() => void refreshBalance()}
        error={balanceStale ? t(balanceOutcomeUnknown ? 'balanceOutcomeUnknown' : 'balanceStale') : error}
      />

      <ResultOverlay
        result={result}
        onClose={() => {
          setResult(null)
          setNextCard(null)
          setCurrentDeal(null)
        }}
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        closeLabel={t('close')}
      />
    </div>
  )
}
